import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { referenceApi } from '@/api/endpoints'
import { Button, EnterChip } from '@/components/ui/Button'
import { TextArea } from '@/components/ui/Field'
import { Combobox } from '@/components/ui/Select'
import { Segmented, YesNo } from '@/components/ui/Toggle'
import { useEnterToContinue, useSaveStep } from '../useWizard'
import { ChoiceWrap, QuestionRow, WizardActions, WizardPanel } from '../WizardShell'
import type { ChecklistPayload, TernaryAnswer } from '@/types/api'

const TERNARY = [
  { value: 'yes' as const, label: 'Yes' },
  { value: 'no' as const, label: 'No' },
  { value: 'prefer_not_to_say' as const, label: 'Prefer not' },
]

const DEFAULTS: ChecklistPayload = {
  openToInPerson: true,
  willingToRelocate: false,
  canStartImmediately: true,
  hasReliableTransportation: true,
  needsAccommodations: 'prefer_not_to_say',
  hasGovernmentClearance: false,
  hasForeignGovernmentTies: false,
  gender: null,
  raceEthnicity: null,
  veteranStatus: 'prefer_not_to_say',
  disabilityStatus: 'prefer_not_to_say',
  additionalNotes: null,
}

interface Props {
  onDone: () => void
  initial?: Partial<ChecklistPayload>
}

export function ChecklistStep({ onDone, initial }: Props) {
  const save = useSaveStep('checklist')
  const [form, setForm] = useState<ChecklistPayload>({ ...DEFAULTS, ...initial })

  const { data: reference } = useQuery({
    queryKey: ['reference'],
    queryFn: referenceApi.all,
    staleTime: Infinity,
  })

  const set = <K extends keyof ChecklistPayload>(key: K, value: ChecklistPayload[K]) =>
    setForm((current) => ({ ...current, [key]: value }))

  const submit = () => {
    if (save.isPending) return
    save.mutate(form, { onSuccess: onDone })
  }
  useEnterToContinue(submit)

  return (
    <>
      <div className="space-y-5">
        <WizardPanel title="Preferences">
          <QuestionRow label="Open to in-person work?">
            <ChoiceWrap>
              <YesNo
                value={form.openToInPerson}
                onChange={(v) => set('openToInPerson', v)}
                aria-label="Open to in-person work"
              />
            </ChoiceWrap>
          </QuestionRow>
          <QuestionRow label="Willing to relocate?">
            <ChoiceWrap>
              <YesNo
                value={form.willingToRelocate}
                onChange={(v) => set('willingToRelocate', v)}
                aria-label="Willing to relocate"
              />
            </ChoiceWrap>
          </QuestionRow>
          <QuestionRow label="Can start immediately?">
            <ChoiceWrap>
              <YesNo
                value={form.canStartImmediately}
                onChange={(v) => set('canStartImmediately', v)}
                aria-label="Can start immediately"
              />
            </ChoiceWrap>
          </QuestionRow>
          <QuestionRow label="Reliable transportation?">
            <ChoiceWrap>
              <YesNo
                value={form.hasReliableTransportation}
                onChange={(v) => set('hasReliableTransportation', v)}
                aria-label="Has reliable transportation"
              />
            </ChoiceWrap>
          </QuestionRow>
          <QuestionRow label="Need workplace accommodations?" hint="Disability, religious, or other." last>
            <ChoiceWrap>
              <Segmented
                options={TERNARY}
                value={form.needsAccommodations}
                onChange={(v) => set('needsAccommodations', v as TernaryAnswer)}
                aria-label="Need workplace accommodations"
              />
            </ChoiceWrap>
          </QuestionRow>
        </WizardPanel>

        <WizardPanel title="Background">
          <QuestionRow label="Active government clearance?">
            <ChoiceWrap>
              <YesNo
                value={form.hasGovernmentClearance}
                onChange={(v) => set('hasGovernmentClearance', v)}
                aria-label="Active government clearance"
              />
            </ChoiceWrap>
          </QuestionRow>
          <QuestionRow label="Family ties to foreign governments?" hint="Employers are required to ask." last>
            <ChoiceWrap>
              <YesNo
                value={form.hasForeignGovernmentTies}
                onChange={(v) => set('hasForeignGovernmentTies', v)}
                aria-label="Family ties to foreign governments"
              />
            </ChoiceWrap>
          </QuestionRow>
        </WizardPanel>

        <WizardPanel title="Diversity &amp; inclusion (optional)">
          <QuestionRow label="Gender">
            <div className="w-full sm:w-[260px]">
              <Combobox
                appearance="box"
                options={(reference?.genderOptions ?? []).map((o) => ({ value: o, label: o }))}
                value={form.gender ?? null}
                onChange={(v) => set('gender', v)}
                placeholder="Select..."
                searchable={false}
              />
            </div>
          </QuestionRow>
          <QuestionRow label="Race / Ethnicity">
            <div className="w-full sm:w-[260px]">
              <Combobox
                appearance="box"
                options={(reference?.raceEthnicityOptions ?? []).map((o) => ({
                  value: o,
                  label: o,
                }))}
                value={form.raceEthnicity ?? null}
                onChange={(v) => set('raceEthnicity', v)}
                placeholder="Select..."
              />
            </div>
          </QuestionRow>
          <QuestionRow label="Veteran status">
            <ChoiceWrap>
              <Segmented
                options={TERNARY}
                value={form.veteranStatus}
                onChange={(v) => set('veteranStatus', v as TernaryAnswer)}
                aria-label="Veteran status"
              />
            </ChoiceWrap>
          </QuestionRow>
          <QuestionRow label="Disability status" hint="Employers must report this in aggregate." last>
            <ChoiceWrap>
              <Segmented
                options={TERNARY}
                value={form.disabilityStatus}
                onChange={(v) => set('disabilityStatus', v as TernaryAnswer)}
                aria-label="Disability status"
              />
            </ChoiceWrap>
          </QuestionRow>
        </WizardPanel>

        <WizardPanel title="Additional info (optional)" bodyClassName="px-4 py-4 sm:px-5">
          <p className="text-[15px] text-ink">Anything else we should know when filling applications?</p>
          <p className="mt-1 text-[13px] text-ink-muted">
            e.g. &ldquo;Notice period 15 days&rdquo;, &ldquo;Willing to travel up to 50%&rdquo;
          </p>
          <TextArea
            value={form.additionalNotes ?? ''}
            onChange={(event) => set('additionalNotes', event.target.value)}
            placeholder="Optional notes..."
            rows={3}
            className="mt-2 text-[16px]"
            aria-label="Additional notes"
          />
        </WizardPanel>
      </div>

      <WizardActions>
        <Button size="lg" className="flex-1 sm:flex-none" onClick={submit} loading={save.isPending}>
          Continue
          <EnterChip />
        </Button>
      </WizardActions>
    </>
  )
}
